import styles from './EmailCapture.module.css';
import { useState, useEffect } from 'react';
import TermsOfService from './TermsOfService.js';
import { validateEmail } from '../utils/validateEmail.js';
import classNames from 'classnames';
import useSearchParamOrDefaultProps from '../hooks/useSearchParamOrDefaultProps.js';
import PropTypes from 'prop-types';
import getRootSelector from '../utils/getRootSelector.js';
import EmailCaptureInput from './EmailCaptureInput.js';
import EmailCaptureButton from './EmailCaptureButton.js';
import EmailCaptureSuccess from './EmailCaptureSuccess.js';
import useRecaptcha from '../hooks/useRecaptcha.js';
import { useMultiStepContext } from './MultiStepComponent.js';
import { useFetch } from 'use-http';
import emailCaptureConfig from './EmailCapture.config.json';
import $t from '../utils/analyticsEventBus.js';
import deriveRuntimeEnvironment from '../utils/deriveRuntimeEnvironment.js';

const defaultData = {
  brand: 'cnbc',
  pid: '33', // Partner id
};

const EmailCapture = ({
  defaultEmail,
  onEmailChange,
  goToNextSteps,
  showEmailCaptureButton,
  newsletterSubscriptions,
  showSubscribedSuccessMessage,
}) => {
  const runtimeEnv = deriveRuntimeEnvironment();
  const { CNBC_REGISTER_BASE_URL, EMAIL_CAPTURE_ENDPOINT } =
    emailCaptureConfig[runtimeEnv];
  const {
    newsletters,
    emailCaptureSource,
    emailCaptureLabel,
    emailCaptureErrorText,
    emailCaptureDelayCloseMilliseconds,
    callToActionButtonDownloadText,
    tos = 'true',
    tosCheckBox = 'true',
    callToActionButtonText,
    analyticsPageId,
    analyticsOutcomeId,
    analyticsTrackingId,
    analyticsOutcomeType,
    analyticsOutcomeDetail,
    analyticsOutcomeSlug,
  } = useSearchParamOrDefaultProps();

  const captchaToken = useRecaptcha();

  const {
    post,
    response,
    loading: requestLoading,
    error: responseError,
  } = useFetch(CNBC_REGISTER_BASE_URL, {
    headers: { 'recaptcha-token': captchaToken },
  });

  const [email, setEmail] = useState(defaultEmail);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [uiError, setUiError] = useState('');

  const { isMultiStep, goToNextStep, currentStep } = useMultiStepContext();
  const isFirstStep = currentStep === 0;

  const analyticsParamOptions = {
    analyticsPageId,
    analyticsOutcomeId,
    analyticsTrackingId,
    analyticsOutcomeType,
    analyticsOutcomeDetail,
    analyticsOutcomeSlug,
  };

  useEffect(() => {
    $t('register', 'phx_zephr_email_submit', {
      allowDuplicate: true,
      remote: true,
    });
  }, []);

  const stepNames = {
    0: 'enter email',
    1: 'select newsletter',
  };

  const triggerAnalyticsEmailSignUpEvent = () => {
    const stepName = stepNames[currentStep];
    const action = `${callToActionButtonText}${
      isMultiStep ? `: step ${currentStep + 1} ${stepName}` : ''
    }`;

    $t('track', 'phx_zephr_email_submit', {
      ...analyticsParamOptions,
      action,
    });
  };

  const onClickCloseEvent = new CustomEvent('onzephrclose', {
    detail: {
      selector: getRootSelector(),
    },
  });

  const delayClose = Number(emailCaptureDelayCloseMilliseconds);

  const handleEmailInputChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (onEmailChange) {
      onEmailChange(newEmail);
    }
  };

  const isTosRequiredAndUnchecked =
    !isChecked && tosCheckBox === 'true' && tos === 'true';

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const isValid = e.target.checkValidity();

    if (isTosRequiredAndUnchecked) {
      setUiError('Please agree to the terms of service.');
      return;
    }

    if (!isValid) {
      e.target.reportValidity();
      return;
    }

    if (!validateEmail(email)) {
      setUiError('Please enter a valid email address.');
      return;
    }

    if (uiError) setUiError('');

    setIsLoading(true);

    const finalSubscriptions = {
      [newsletters]: true,
      ...newsletterSubscriptions,
    };

    const data = {
      ...defaultData,
      email: email,
      source: emailCaptureSource,
      subscriptions: finalSubscriptions,
    };

    try {
      await post(EMAIL_CAPTURE_ENDPOINT, data);

      if (response.ok) {
        triggerAnalyticsEmailSignUpEvent();

        setIsSubscribed(true);
        showSubscribedSuccessMessage(true);
        setIsLoading(false);

        // call the goToNextSteps callback if provided
        if (goToNextSteps) {
          goToNextStep();
        }
        // Some overlays have a delayed close after the user subscribes
        if (!callToActionButtonDownloadText && !goToNextSteps) {
          setTimeout(() => window.dispatchEvent(onClickCloseEvent), delayClose);
        }
      } else {
        const errorMessage =
          response?.data?.user_error_message ?? emailCaptureErrorText;
        setUiError(errorMessage ?? responseError.message);
        setIsLoading(false);
      }
    } catch (error) {
      setUiError(error.message ?? emailCaptureErrorText);
      setIsLoading(false);
    }
  };

  const isButtonDisabled = isLoading || requestLoading;

  const emailCaptureLabelClassname = classNames(styles.emailCaptureLabel, {
    [styles.emailCaptureLabelPaddingBottom]: emailCaptureLabel,
  });

  return (
    <div className={styles.emailCaptureContainer}>
      {isSubscribed ? (
        <EmailCaptureSuccess />
      ) : (
        <form onSubmit={handleFormSubmit} className={styles.emailCaptureForm}>
          <div className={emailCaptureLabelClassname}>{emailCaptureLabel}</div>
          <div className={styles.emailCaptureWrapper}>
            {isFirstStep && (
              <EmailCaptureInput
                value={email}
                onChange={handleEmailInputChange}
              />
            )}

            {tos === 'true' && isMultiStep && showEmailCaptureButton && (
              <TermsOfService
                isChecked={isChecked}
                setIsChecked={setIsChecked}
              />
            )}

            {showEmailCaptureButton && (
              <EmailCaptureButton
                isLoading={isLoading}
                isDisabled={isButtonDisabled}
              />
            )}
          </div>
          {tos === 'true' && !isMultiStep && showEmailCaptureButton && (
            <TermsOfService isChecked={isChecked} setIsChecked={setIsChecked} />
          )}
          {uiError && <div className={styles.emailCaptureError}>{uiError}</div>}
        </form>
      )}
    </div>
  );
};

EmailCapture.displayName = 'EmailCapture';

EmailCapture.defaultProps = {
  defaultEmail: '',
  goToNextSteps: false,
  newsletterSubscriptions: {},
  onEmailChange: () => {},
  showEmailCaptureButton: true,
  showSubscribedSuccessMessage: () => {},
};

EmailCapture.propTypes = {
  defaultEmail: PropTypes.string,
  goToNextSteps: PropTypes.bool,
  newsletterSubscriptions: PropTypes.object,
  onEmailChange: PropTypes.func,
  showEmailCaptureButton: PropTypes.bool,
  showSubscribedSuccessMessage: PropTypes.func,
};

export default EmailCapture;
